// Edits to one open record stay in the editor until Save, as in Articles: one save, one version
import { useCallback, useEffect, useState } from "react"

// The store operations an editor's fields already call, applied to the draft instead of the saved store
export interface DraftOps<S, K extends string> {
  bucket: Record<K, string>
  getEntity: (store: S, kind: K, id: string) => any
  updateEntity: (store: S, kind: K, id: string, fields: Record<string, any>) => S
  setTranslation: (store: S, kind: K, id: string, locale: string, patch: Record<string, any>) => S
}

interface Draft {
  // The saved record when editing started, to tell if it changed elsewhere
  base: any
  entity: any
}

// Drafts outlive the editor, so switching records doesn't lose them
const drafts = new Map<string, Draft>()
const listeners = new Set<() => void>()
const notify = () => listeners.forEach(l => l())

let guarded = false
function guardUnload() {
  if (guarded || typeof window === "undefined") return
  guarded = true
  window.addEventListener("beforeunload", e => {
    if (!drafts.size) return
    e.preventDefault()
    e.returnValue = ""
  })
}

export const hasUnsavedDraft = (section: string, kind: string, id: string) => drafts.has(`${section}:${kind}:${id}`)

const withEntity = <S,>(store: S, bucket: string, id: string, entity: any): S =>
  ({ ...store, [bucket]: { ...(store as any)[bucket], [id]: entity } })

export function useRecordDraft<S, K extends string>(opts: {
  section: string
  store: S
  kind: K
  id: string
  ops: DraftOps<S, K>
  // Saves the record as one change; false when it was refused, so the draft is kept
  onCommit: (kind: K, id: string, entity: any) => boolean
  // Edits to any other record go straight through
  onUpdate: (kind: K, id: string, fields: Record<string, any>) => void
  onSetTranslation: (kind: K, id: string, locale: string, patch: Record<string, any>) => void
}) {
  const { section, store, kind, id, ops } = opts
  const key = `${section}:${kind}:${id}`
  const [, rerender] = useState(0)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    guardUnload()
    const l = () => rerender(n => n + 1)
    listeners.add(l)
    return () => { listeners.delete(l) }
  }, [])
  useEffect(() => { setError(null) }, [key])

  const saved = ops.getEntity(store, kind, id)
  const draft = drafts.get(key)
  // What the editor shows: the saved store with this record swapped for its draft
  const view = draft && saved ? withEntity(store, ops.bucket[kind], id, draft.entity) : store

  const apply = useCallback((change: (s: S) => S) => {
    const current = drafts.get(key)
    const from = current && saved ? withEntity(store, ops.bucket[kind], id, current.entity) : store
    let next: S
    try {
      next = change(from)
    } catch (e: any) {
      setError(e?.message || String(e))
      return
    }
    setError(null)
    drafts.set(key, { base: current?.base ?? saved, entity: ops.getEntity(next, kind, id) })
    notify()
  }, [key, store, saved, ops, kind, id])

  const onUpdate = useCallback((k: K, i: string, fields: Record<string, any>) => {
    if (k !== kind || i !== id) return opts.onUpdate(k, i, fields)
    apply(s => ops.updateEntity(s, k, i, fields))
  }, [kind, id, apply, ops, opts])

  const onSetTranslation = useCallback((k: K, i: string, locale: string, patch: Record<string, any>) => {
    if (k !== kind || i !== id) return opts.onSetTranslation(k, i, locale, patch)
    apply(s => ops.setTranslation(s, k, i, locale, patch))
  }, [kind, id, apply, ops, opts])

  const discard = useCallback(() => {
    drafts.delete(key)
    setError(null)
    notify()
  }, [key])

  const save = useCallback(() => {
    const current = drafts.get(key)
    if (!current) return
    if (opts.onCommit(kind, id, current.entity)) {
      drafts.delete(key)
      notify()
    }
  }, [key, kind, id, opts])

  return {
    store: view,
    dirty: !!draft,
    // Saved by someone (or the assistant) since this draft began; saving replaces it
    changedElsewhere: !!draft && !!saved && draft.base?.updatedAt !== saved.updatedAt,
    error,
    onUpdate,
    onSetTranslation,
    save,
    discard,
  }
}

const humanize = (k: string) => k.replace(/Ids?$/, "").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase()
// Missing, false, "", [] and {} all mean "not set", so a default filled in isn't a change
const isUnset = (v: unknown): boolean =>
  v == null || v === false || v === "" || (Array.isArray(v) && !v.length)
  || (typeof v === "object" && !Array.isArray(v) && Object.values(v as object).every(isUnset))
// Key order doesn't matter: a record round-tripped through the database comes back reordered
const stable = (v: unknown): string => {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`
  if (v && typeof v === "object") {
    return `{${Object.keys(v as object).sort().map(k => `${JSON.stringify(k)}:${stable((v as any)[k])}`).join(",")}}`
  }
  return JSON.stringify(v ?? null)
}
const same = (a: unknown, b: unknown) => (isUnset(a) && isUnset(b)) || stable(a) === stable(b)

// "Edited name, hours and description (es-ES)": what one save changed, for the history
export function describeEdit(before: any, after: any): string {
  if (!before) return "Edited"
  const ignore = new Set(["translations", "updatedAt", "createdAt", "id"])
  const fields = Object.keys({ ...before, ...after })
    .filter(k => !ignore.has(k) && !same(before[k], after[k]))
    .map(humanize)
  const bt = before.translations ?? {}
  const at = after.translations ?? {}
  for (const loc of Object.keys({ ...bt, ...at })) {
    const changed = Object.keys({ ...bt[loc], ...at[loc] })
      .filter(k => k !== "updatedAt" && k !== "locale" && !same(bt[loc]?.[k], at[loc]?.[k]))
      .map(humanize)
    if (changed.length) fields.push(`${changed.join(", ")} (${loc})`)
  }
  if (!fields.length) return "Saved with no changes"
  const list = fields.length > 1 ? `${fields.slice(0, -1).join(", ")} and ${fields[fields.length - 1]}` : fields[0]
  return `Edited ${list}`
}
